import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { acquireVetTabPresence } from "@/lib/vetTabPresence";

// Global vet "I'm available" heartbeat.
//
// Why this exists:
// The dashboard's AvailableNowToggle was the only place sending heartbeats
// (every 30s) into vet_availability_now.last_heartbeat_at. As soon as the
// vet navigated to ANY other GetPetVet page (Settings, a pet passport, the
// landing page, etc.), the heartbeat stopped - and after 5 minutes the
// server-side cron mark_stale_vets_offline() flipped them to is_available=false
// even though their screen wasn't locked and they were still actively using
// the site.
//
// This hook moves the heartbeat to the app root so it keeps running anywhere
// a logged-in vet is browsing. It also sends a fire-and-forget "offline"
// beacon on pagehide so closing the tab doesn't leave them looking online
// for up to 5 minutes.
//
// Important: this hook does NOT decide whether the vet is online. It only
// keeps the heartbeat alive when their toggle is already on. Going on/off,
// screen-lock auto-offline, auto-resume, etc. all still live in
// AvailableNowToggle. We deliberately re-check the DB row before each beat
// so we don't bring an offline vet back to life.

const HEARTBEAT_INTERVAL_MS = 30_000;
// Re-check the toggle state from DB this often. If the vet manually went
// offline elsewhere, we want to stop heartbeating quickly.
const STATE_REFRESH_INTERVAL_MS = 60_000;

interface VetAvailabilityRow {
  practice_id: string;
  is_available: boolean;
  accepts_video: boolean;
  accepts_phone: boolean;
}

// Cross-tab presence is centralised in `lib/vetTabPresence.ts` so this hook
// and AvailableNowToggle's screen-lock auto-offline ask the exact same
// question - "are there other live GetPetVet tabs for me right now?" - and
// can't drift out of sync.

export function useVetHeartbeat() {
  const { user, session } = useAuth();
  const { role } = useUserRole();
  const stateRef = useRef<VetAvailabilityRow | null>(null);

  // Hard kill-switch: when Supabase fires SIGNED_OUT we proactively wipe the
  // local availability snapshot AND remove every realtime channel this app
  // has open. The per-effect cleanups below already drop the heartbeat's own
  // channel when `user` becomes null, but a global removeAllChannels() makes
  // sure no leaked subscription from any other component keeps a websocket
  // alive after logout - critical for a vet who logs out on a shared device.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        stateRef.current = null;
        void supabase.removeAllChannels();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || role !== "vet") {
      stateRef.current = null;
      return;
    }
    const userId = user.id;
    let alive = true;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let refreshTimer: ReturnType<typeof setInterval> | null = null;

    const refreshState = async () => {
      const { data } = await supabase
        .from("vet_availability_now")
        .select("practice_id, is_available, accepts_video, accepts_phone")
        .eq("user_id", userId)
        .maybeSingle();
      if (!alive) return;
      stateRef.current = (data as VetAvailabilityRow | null) ?? null;
    };

    const beat = async () => {
      const row = stateRef.current;
      if (!row || !row.is_available) return;
      // Note: we deliberately do NOT skip when document.hidden. Heartbeats
      // mean "I'm still logged in", not "I'm looking at the screen". Pet
      // owners shouldn't lose access to a vet just because the vet tabbed
      // away to read an email. Screen-lock auto-offline (in
      // AvailableNowToggle) is what handles "actually unavailable".
      await supabase
        .from("vet_availability_now")
        .upsert(
          {
            user_id: userId,
            practice_id: row.practice_id,
            is_available: true,
            accepts_video: row.accepts_video,
            accepts_phone: row.accepts_phone,
            last_heartbeat_at: new Date().toISOString(),
          },
          { onConflict: "user_id" },
        );
    };

    // Track pending "going offline" verification - see grace-window comment
    // below. We don't import the shared hook here because this file lives
    // outside React render flow for parts of its lifecycle; a small inline
    // timer is enough.
    const OFFLINE_CONFIRM_DELAY_MS = 8_000;
    let pendingOfflineTimer: ReturnType<typeof setTimeout> | null = null;
    const cancelPendingOffline = () => {
      if (pendingOfflineTimer) {
        clearTimeout(pendingOfflineTimer);
        pendingOfflineTimer = null;
      }
    };

    const channel = supabase
      .channel(`global-vet-heartbeat:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "vet_availability_now", filter: `user_id=eq.${userId}` },
        (payload) => {
          const row = (payload.new ?? payload.old) as Partial<VetAvailabilityRow> | undefined;
          if (!row || !row.practice_id) return;
          const nextAvailable = !!row.is_available;
          const wasAvailable = stateRef.current?.is_available === true;
          // Always keep accept_* + practice_id fresh.
          const baseUpdate: VetAvailabilityRow = {
            practice_id: row.practice_id,
            is_available: stateRef.current?.is_available ?? nextAvailable,
            accepts_video: !!row.accepts_video,
            accepts_phone: !!row.accepts_phone,
          };
          stateRef.current = baseUpdate;
          if (nextAvailable) {
            // Going online: trust immediately, drop any pending verify.
            cancelPendingOffline();
            stateRef.current = { ...baseUpdate, is_available: true };
            return;
          }
          // Going offline while we believed we were available - could be the
          // server reaper flipping us off due to a missed heartbeat. Re-check
          // after a short delay; if a heartbeat lands in the meantime, the
          // DB will report true again and we keep heartbeating.
          if (!wasAvailable) {
            stateRef.current = { ...baseUpdate, is_available: false };
            return;
          }
          if (pendingOfflineTimer) return;
          pendingOfflineTimer = setTimeout(async () => {
            pendingOfflineTimer = null;
            const { data } = await supabase
              .from("vet_availability_now")
              .select("practice_id, is_available, accepts_video, accepts_phone")
              .eq("user_id", userId)
              .maybeSingle();
            if (!alive) return;
            const confirmed = (data as VetAvailabilityRow | null) ?? null;
            if (confirmed && confirmed.is_available === false) {
              stateRef.current = confirmed;
            }
            // else: heartbeat caught up, ignore the stale realtime "false".
          }, OFFLINE_CONFIRM_DELAY_MS);
        },
      )
      .subscribe();

    void refreshState().then(() => void beat());

    // Hidden-tab resilience: browsers throttle setInterval in background tabs
    // (Chrome: ~1 call/min; Safari iOS: often frozen entirely). A dedicated
    // Web Worker keeps a precise 30s cadence even when the page is hidden,
    // so heartbeats land well inside the 10-min stale window. We still keep
    // the main-thread interval as a belt-and-braces fallback if the Worker
    // is blocked (CSP / sandbox).
    let worker: Worker | null = null;
    try {
      const workerSrc = "let h=null;onmessage=(e)=>{const d=e.data;if(d&&d.type==='start'){if(h)clearInterval(h);h=setInterval(()=>postMessage('beat'),d.intervalMs);}else if(d&&d.type==='stop'){if(h)clearInterval(h);h=null;}};";
      const blob = new Blob([workerSrc], { type: "application/javascript" });
      const url = URL.createObjectURL(blob);
      worker = new Worker(url);
      URL.revokeObjectURL(url);
      worker.onmessage = () => { void beat(); };
      worker.postMessage({ type: "start", intervalMs: HEARTBEAT_INTERVAL_MS });
    } catch {
      worker = null;
    }
    heartbeatTimer = setInterval(() => { void beat(); }, HEARTBEAT_INTERVAL_MS);
    refreshTimer = setInterval(() => { void refreshState(); }, STATE_REFRESH_INTERVAL_MS);

    // Beat immediately when the page becomes visible OR is restored from
    // bfcache (mobile Safari fires pageshow with persisted=true). Either
    // event means the user is back; closing the gap right away keeps the
    // "online now" badge accurate without waiting for the next tick.
    const onVisible = () => {
      if (document.visibilityState === "visible") void beat();
    };
    const onPageShow = () => { void beat(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      alive = false;
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      if (refreshTimer) clearInterval(refreshTimer);
      if (worker) {
        try { worker.postMessage({ type: "stop" }); } catch { /* ignore */ }
        worker.terminate();
      }
      cancelPendingOffline();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
      void supabase.removeChannel(channel);
    };
  }, [user, role]);

  // pagehide: closing/refreshing the tab. Use fetch with keepalive so the
  // request actually completes after the page is gone. supabase-js doesn't
  // expose keepalive, so we hit PostgREST directly with the access token.
  // Only beacon when this is the LAST live vet tab - otherwise closing one
  // of several open GetPetVet tabs would wrongly take the vet offline.
  //
  // Refresh-grace window: a browser refresh fires pagehide → page reload
  // typically completes within 1-2 seconds. We don't want a refresh to flip
  // the vet offline (and toggle the GetVideoVet switch), so we stamp
  // localStorage on pagehide and check it on mount. If we're back within
  // REFRESH_GRACE_MS we silently re-assert is_available=true (no toast,
  // no audit churn). If the gap exceeds the grace window, we leave them
  // offline AND insert a notification so the bell chimes - they're now
  // explicitly aware they need to toggle back on.
  useEffect(() => {
    if (!user || role !== "vet") return;
    const userId = user.id;
    const accessToken = session?.access_token;
    if (!accessToken) return;
    const rpcUrl = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/mark_vet_offline`;
    const apiKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const REFRESH_GRACE_MS = 10_000;
    const lastSeenKey = `vet-last-seen:${userId}`;

    const presence = acquireVetTabPresence(userId);

    // ---- On-mount: refresh-grace recovery ---------------------------------
    // Runs once when this hook mounts (which happens on every full page load,
    // including a hard refresh). We compare the stamp written by the previous
    // page's pagehide handler to "now":
    //   - within grace AND vet was available  → silently restore is_available
    //   - past grace AND vet is currently offline AND was previously available
    //     → leave offline, insert a notification (bell + chime)
    void (async () => {
      let lastSeenAt = 0;
      let wasAvailable = false;
      try {
        const raw = localStorage.getItem(lastSeenKey);
        if (raw) {
          const parsed = JSON.parse(raw) as { at?: number; wasAvailable?: boolean; practiceId?: string; acceptsVideo?: boolean; acceptsPhone?: boolean };
          lastSeenAt = typeof parsed.at === "number" ? parsed.at : 0;
          wasAvailable = !!parsed.wasAvailable;
          var lastPracticeId = parsed.practiceId;
          var lastAcceptsVideo = !!parsed.acceptsVideo;
          var lastAcceptsPhone = !!parsed.acceptsPhone;
        }
      } catch { /* ignore parse errors */ }
      // Always clear the stamp once read so a stale value can't haunt the
      // next session.
      try { localStorage.removeItem(lastSeenKey); } catch { /* ignore */ }
      if (!lastSeenAt || !wasAvailable) return;
      const gap = Date.now() - lastSeenAt;
      // Read current DB state so we know whether the beacon (or cron) already
      // flipped us offline.
      const { data } = await supabase
        .from("vet_availability_now")
        .select("practice_id, is_available, accepts_video, accepts_phone")
        .eq("user_id", userId)
        .maybeSingle();
      const nowOffline = !data?.is_available;
      const practiceId = data?.practice_id ?? lastPracticeId;
      if (!practiceId) return;
      if (gap <= REFRESH_GRACE_MS && nowOffline) {
        // Silent restore - the vet just refreshed and we shouldn't have
        // marked them offline at all.
        await supabase
          .from("vet_availability_now")
          .upsert({
            user_id: userId,
            practice_id: practiceId,
            is_available: true,
            accepts_video: data?.accepts_video ?? lastAcceptsVideo,
            accepts_phone: data?.accepts_phone ?? lastAcceptsPhone,
            last_heartbeat_at: new Date().toISOString(),
          }, { onConflict: "user_id" });
        // Refresh local snapshot so the heartbeat loop keeps beating.
        stateRef.current = {
          practice_id: practiceId,
          is_available: true,
          accepts_video: data?.accepts_video ?? lastAcceptsVideo,
          accepts_phone: data?.accepts_phone ?? lastAcceptsPhone,
        };
        return;
      }
      if (gap > REFRESH_GRACE_MS && nowOffline) {
        // Real away period - notify in the bell so they hear the chime and
        // can re-enable when ready.
        const seconds = Math.round(gap / 1000);
        await supabase.from("notifications").insert({
          user_id: userId,
          type: "vet_availability",
          title: "You were taken offline on GetVideoVet",
          message: `You were away for about ${seconds}s. Toggle 'I'm available now' back on when you're ready to take calls.`,
          link: "/vet-dashboard",
        });
      }
    })();

    const sendOfflineBeacon = () => {
      const row = stateRef.current;
      if (!row || !row.is_available) return;
      // Stamp last-seen so the next page load can decide refresh-grace vs
      // real absence. We do this BEFORE the "other tabs" check so even tabs
      // that aren't the last one still record their state - useful if the
      // remaining tab also closes seconds later.
      try {
        localStorage.setItem(lastSeenKey, JSON.stringify({
          at: Date.now(),
          wasAvailable: true,
          practiceId: row.practice_id,
          acceptsVideo: row.accepts_video,
          acceptsPhone: row.accepts_phone,
        }));
      } catch { /* ignore quota errors */ }
      // Tell siblings we're leaving first so a race-condition tab that's also
      // closing right now can re-evaluate "am I the last one?".
      presence.announceLeaving();
      if (presence.hasOtherLiveTabs()) return;
      try {
        // Route through the audit-aware RPC so the admin availability audit
        // log records "client-disconnect" instead of "unknown".
        void fetch(rpcUrl, {
          method: "POST",
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            apikey: apiKey,
            Authorization: `Bearer ${accessToken}`,
            Prefer: "return=minimal",
          },
          body: JSON.stringify({
            _reason: "client-disconnect",
            _source: "beacon",
            _detail: "Last live vet tab closed (pagehide beacon).",
          }),
        });
      } catch {
        // best-effort; the 10-min cron is the safety net
      }
    };

    window.addEventListener("pagehide", sendOfflineBeacon);
    return () => {
      window.removeEventListener("pagehide", sendOfflineBeacon);
      presence.release();
    };
  }, [user, role, session?.access_token]);
}
